import type { DragObject, ItemsDroppedEvent } from '@mimir/web/pages/search/components/search-page-interfaces';
import type { DroppableItem } from '@mimir/client-common/util/droppedItems';
import { getGlobalStore } from '@mimir/web/store';
import Vue from 'vue';
import { getFolderFromCacheOrServer } from '@mimir/web/util/folder';
import { moveFolder as _moveFolder, addItemToFolder } from '@mimir/web/api/globalClientFolder';
import { isFolderDto, type FolderDto, type FolderPathSegmentDto, type FolderViewModel } from '@mimir/common/dto/folderModels';
import type { FolderSearchResultHit, Item } from '@mimir/common/dto/searchResultHit';
import { isItem } from '@mimir/common/dto/searchResultHit';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { MovementRequest} from './movement';
import { MovementStatus } from './movement';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import eventBus, { showToast, EVENTS, TOAST } from '@mimir/web/eventBus';
import { httpErrorStatus, isAxiosError } from '@mimir/common/util/httpError';
import { getCurrentSearchType, SearchType } from '../search/util';
import { cacheFolderPath, folders, getFolderFromCache, getUncachedPathStringifiedForFolder, invalidatePathCacheForFolder, moveFolder } from '@mimir/web/globals/folders';
import { tryToGetFoldersToRootFromCache, checkIfRoot } from '@mimir/web/util/path-to-root';
import { showFolderMoveWaitDialog } from '@mimir/web/dialogs/folderMoveWait';

export const moveContent = async (request: MovementRequest, doCopy: boolean, sourceId?: string, folderParents?: string[]): Promise<MovementRequest> => {

  if (request.targetFolder.id === sourceId || (folderParents && folderParents.includes(request.targetFolder.id))) {
    request.errorMessage = 'Item already present in target folder';
    request.status = MovementStatus.ERROR;
    return request;
  }

  try {
    request.status = MovementStatus.LOADING;
    await addItemToFolder(request.targetFolder.id, request.id, doCopy ? undefined : sourceId);
    if (!doCopy && request.item && request.targetFolder.visibleTo) {
      Vue.set(request.item, 'visibleTo', request.targetFolder.visibleTo);
    }
    request.status = MovementStatus.OK;
    return request;
  } catch (err: any) {
    let errorMessage = `Something went wrong when moving item to folder: ${ request.targetFolder.name }`;
    if (httpErrorStatus(err) === 404) {
      errorMessage = 'Either item or target folder does not exist.';
    }
    if (httpErrorStatus(err) === 403) {
      errorMessage = 'User does not have permission to move the item.';
    }
    request.errorMessage = errorMessage;
    return request;
  }
};

const moveFolderWithoutImplicitVisibilityUpdate = async (request: MovementRequest): Promise<MovementRequest> => {

  if (request.targetFolder.id === request.id) {
    request.errorMessage = 'Can not move folder onto itself.';
    request.status = MovementStatus.ERROR;
    return request;
  }

  try {
    request.status = MovementStatus.LOADING;
    await _moveFolder(request.id, request.targetFolder.id, false);
    request.status = MovementStatus.OK;
    return request;
  } catch (err: any) {
    let userReadableMessage = `Something went wrong when moving folder '${request.name}' to folder: '${ request.targetFolder.name }'`;
    if (isAxiosError(err)) {
      const status = err.response?.status;
      const serverMessage: string | undefined = err.response?.data?.error?.message;
      switch (status) {
        case 400: userReadableMessage = serverMessage || userReadableMessage; break;
        case 403: userReadableMessage = 'User does not have permission to move the folder.'; break;
        case 404: userReadableMessage = serverMessage || userReadableMessage; break;
        case 409: userReadableMessage = serverMessage || userReadableMessage; break;
        case 503: userReadableMessage = 'The server is busy at this moment. Please retry after a while'; break;
      }
    }

    request.status = MovementStatus.ERROR;
    request.errorMessage = userReadableMessage;
    return request;
  }
};

export const getOpTypeOnCtrlOrAltHold = (isHoldingCtrlOrAlt: boolean, isMoveDefault?: boolean): 'Move' | 'Copy' => {
  if (getCurrentSearchType() === SearchType.SEARCH_VIEW) {
    // from search results we only allow user to copy.
    return 'Copy';
  }

  if (isMoveDefault) {
    return isHoldingCtrlOrAlt ? 'Copy' : 'Move';
  }

  return isHoldingCtrlOrAlt ? 'Move' : 'Copy';
};

export const isMoveOp = () => {
  const content: DragObject | null = getGlobalStore().getters.dragObject;
  const dragWithCtrlOrAlt: boolean = getGlobalStore().getters.dragWithCtrlOrAlt;

  if (content === null) return false;

  if (content.type === 'related') return false;

  const isActualItem = isItem(content.item) && content.item.itemType !== ItemType.FOLDER;
  if (!isActualItem) return true;

  return getOpTypeOnCtrlOrAltHold(dragWithCtrlOrAlt, getGlobalStore().getters.tenantConfig.isMoveDefault) === 'Move';
};

export const getFolderIcon = (isHovered: boolean, isShortcut?: boolean, folderLock?: boolean) => {
  if (folderLock) return 'mdi-folder-lock';
  if (isShortcut) return 'bookmark';
  if (!isHovered) return 'folder';

  return isMoveOp() ? 'folder_open' : 'create_new_folder';
};

export const isAnyFolderInSelectedItems = (selectedItems: DroppableItem[]) => {
  return selectedItems.some(item => isItem(item) && item.itemType === ItemType.FOLDER);
};

export async function getPathStringifiedForFolder(folderId: string) {
  const folders_ = tryToGetFoldersToRootFromCache(folderId);
  if (folders_.length > 0 && checkIfRoot(folders_[0])) {
    const path = folders_.map(folder => {
      return { id: folder.id, name: folder.name } as FolderPathSegmentDto;
    });
    cacheFolderPath(folderId, path);
  }
  return getUncachedPathStringifiedForFolder(folderId);
}

export const doMoveOrCopyOperation = async (payload: ItemsDroppedEvent, selectedItems: DroppableItem[]) => {
  if (selectedItems.length === 0) {
    showToast('Nothing to copy/move.', TOAST.INFO);
    return;
  }

  const { folderId, name, isHoldingCtrlOrAlt, dragObject } = payload;

  const doCopy = getOpTypeOnCtrlOrAltHold(isHoldingCtrlOrAlt, getGlobalStore().getters.tenantConfig.isMoveDefault) === 'Copy';
  const targetFolder = await getFolderFromCacheOrServer(payload.folderId);
  const visibleTo = targetFolder.visibleTo;
  const promises: Promise<MovementRequest>[] = [];
  const shouldMakeUserWait = selectedItems.length > 5;

  let sourceId: string | undefined = undefined;

  // If not dealing with a related item, we need to grab the current parent folder ID in different places
  // depending on whether it is a folder view model from the folder tree, or a search hit from search.
  if (payload.dragObject.type !== 'related') {
    sourceId = isFolderViewModel(dragObject.item) ? dragObject.item.parent : folders.currentFolder?.id;
  }

  const movementRequests: MovementRequest[] = [];
  // If we detect we'll have more than 5 promises, let's make the user wait for completion
  // to avoid users experiencing a locked UI due to outstanding requests.
  if (shouldMakeUserWait) {
    showFolderMoveWaitDialog(movementRequests);
  }

  const idsToRemoveFromSearch: string[] = [];

  for (const searchHit of selectedItems.filter(isItem)) {
    if (isFolderDto(searchHit)) {

      const movementRequest: MovementRequest = Object.assign({}, {
        id: searchHit.id,
        name: searchHit.name ?? '',
        status: MovementStatus.PENDING,
        targetFolder: {
          name,
          id: folderId,
          visibleTo
        },
        type: ItemType.FOLDER,
        errorMessage: null,
        item: searchHit as FolderDto
      });

      let isSameAsCurrent = false;

      if (isFolderViewModel(searchHit)) {
        isSameAsCurrent = movementRequest.targetFolder.id === searchHit.parent;
      } else {
        isSameAsCurrent = searchHit.folderParents && searchHit.folderParents.includes(movementRequest.targetFolder.id);
      }

      if (isSameAsCurrent) {
        showToast('Folder is already present in target folder', TOAST.INFO);
        continue;
      }

      const move: Promise<MovementRequest> = new Promise((resolve, reject) => {
        moveFolderWithoutImplicitVisibilityUpdate(movementRequest)
        .then(async request => {
          if (request.status !== MovementStatus.ERROR) {
            const cachedTargetFolder = getFolderFromCache(folderId);
            const alreadyExistInTargetFolder = cachedTargetFolder?.subTrees?.find(childNode => childNode.id === searchHit.id);
            /*
              If cached, it means the folder was already moved by a websocket event.
              So, we only make the update if it hasn't been cached.
            */
            if (!alreadyExistInTargetFolder) {
              await moveFolder(sourceId!, folderId, searchHit.id);
              invalidatePathCacheForFolder(searchHit.id);
            }
          }
          resolve(request);
        })
        .catch((error: any) => reject(error));
      });

      if (shouldMakeUserWait) {
        movementRequests.push(movementRequest);
      }

      promises.push(move);

      if (searchHit.id !== folderId) {
        idsToRemoveFromSearch.push(searchHit.id);
      }
    } else {
      // Stop this early so we avoid removing this item unnecessarily from search optimistically.
      if (!shouldMakeUserWait && searchHit.folderParents && searchHit.folderParents.includes(folderId)) {
        showToast('Item is already present in target folder', TOAST.INFO);
        continue;
      }

      const movementRequest: MovementRequest = Object.assign({}, {
        id: searchHit.id,
        name: getItemSystemDefaultField(searchHit, SystemDefaultField.Title),
        status: MovementStatus.PENDING,
        targetFolder: {
          name,
          id: folderId,
          visibleTo
        },
        type: searchHit.itemType,
        errorMessage: null,
        item: searchHit as Item
      });

      if (shouldMakeUserWait) {
        movementRequests.push(movementRequest);
      }

      promises.push(moveContent(movementRequest, doCopy, sourceId, searchHit.folderParents));

      if (!doCopy) {
        idsToRemoveFromSearch.push(searchHit.id);
      }
    }
  }

  if (targetFolder && isFolderViewModel(targetFolder)) {
    targetFolder.loading = true;
  }

  if (sourceId) {
    getGlobalStore().commit('removeIdsFromSearchResult', { onlyFolders: doCopy, idsToRemove: idsToRemoveFromSearch });
  }

  // this is a very specific use case where you drag a folder on the 'currentFolder'
  // so the 'currentFolder' view should insert the folder in the searchResult
  if (
    folderId === folders.currentFolder?.id
    && selectedItems.length === 1
    && selectedItems.filter(isItem)[0].itemType === ItemType.FOLDER
  ) {
    getGlobalStore().commit('injectFolderIntoSearchResult', selectedItems[0]);
  }

  const results = await Promise.all(promises);
  const failed = results.filter(request => request.errorMessage !== null);

  if (failed.length > 0) {
    // Let's not try to fix the optimistic removal from search, and rather just do a hard refresh on the current page.
    eventBus.emit(EVENTS.REFRESH_CURRENT_SEARCH);

    // Only show toast when we are not showing the dialog - otherwise the user can inspect the errors there.
    // We show the error from the promise if the user was only moving one item,
    // otherwise a generic error message will be displayed.
    if (!shouldMakeUserWait) {
      const genericErrorMessage = getErrorMessageForMoveOrCopyOperation(failed, doCopy);
      showToast(failed.length === 1 ? failed[0].errorMessage! : genericErrorMessage, TOAST.ERROR);
    }
  }

  if (targetFolder && isFolderViewModel(targetFolder)) {
    targetFolder.loading = false;
  }

  // Only clear selection when we're moving items around.
  if (sourceId && !doCopy) {
    getGlobalStore().commit('clearSelectedSearchItems', { root: true });
  }

  return;
};

/**
 * @returns a message that describes the number of items that failed to move or copy.
 */
const getErrorMessageForMoveOrCopyOperation = (failed: MovementRequest[], doCopy: boolean) => {
  if (failed.length === 0) return '';

  const failedFolderCount = failed.filter(request => request.type === ItemType.FOLDER).length;
  const failedItemCount = failed.length - failedFolderCount;
  let message = '';

  if (failedItemCount > 0) {
    message += `${failedItemCount} item${failedItemCount > 1 ? 's' : ''} failed to ${doCopy ? 'copy' : 'move'}`;
  }

  if (failedFolderCount > 0) {
    if (message) message += ' and ';
    message += `${failedFolderCount} folder${failedFolderCount > 1 ? 's' : ''} failed to move`;
  }

  return `${message}.`;
};

export const isFolderViewModel = (folder: Folder | DroppableItem): folder is FolderViewModel => {
  return 'loading' in folder && !('folderParents' in folder);
};

export type Folder =  FolderViewModel | FolderDto | FolderSearchResultHit;
